import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppSidebar } from './app-sidebar';
import { SiteHeader } from './site-header';
import { SidebarInset, SidebarProvider } from './ui/sidebar';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MUSIC_CATEGORIES,
  type MusicCategory,
  type CreateMusicTrack,
  addMusicTrack,
  generateTrackId,
  uploadFile,
  updateMusicTrack,
} from '@/services/musicTracksService';
import { readMp3Metadata, type Mp3Metadata } from '@/utils/readMp3Metadata';
import { writeMp3Metadata, buildComment } from '@/utils/writeMp3Metadata';
import { cropImageToSquare } from '@/utils/cropImageToSquare';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { ArrowLeft, Loader2, Music, Upload, X } from 'lucide-react';

interface PendingTrack {
  id: string;
  file: File;
  metadata: Mp3Metadata;
  category: MusicCategory;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

function formatDuration(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '--:--';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function UploadTracksPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [tracks, setTracks] = useState<PendingTrack[]>([]);
  const [globalCategory, setGlobalCategory] = useState<MusicCategory>('ambient');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const addFiles = useCallback(async (files: FileList | File[]) => {
    const fileList = Array.from(files).filter((f) =>
      f.name.toLowerCase().endsWith('.mp3')
    );
    if (fileList.length === 0) return;

    const newTracks: PendingTrack[] = await Promise.all(
      fileList.map(async (file) => {
        const metadata = await readMp3Metadata(file);
        return {
          id: `${file.name}-${Date.now()}-${Math.random()}`,
          file,
          metadata,
          category: globalCategory,
          status: 'pending' as const,
        };
      })
    );

    setTracks((prev) => [...prev, ...newTracks]);
  }, [globalCategory]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files) addFiles(files);
      e.target.value = '';
    },
    [addFiles]
  );

  const removeTrack = useCallback((id: string) => {
    setTracks((prev) => {
      const track = prev.find((t) => t.id === id);
      if (track?.metadata.pictureUrl) {
        URL.revokeObjectURL(track.metadata.pictureUrl);
      }
      return prev.filter((t) => t.id !== id);
    });
  }, []);

  const uploadAll = useCallback(async () => {
    if (!currentUser) return;

    const pending = tracks.filter((t) => t.status === 'pending');
    if (pending.length === 0) return;

    setUploadingCount(pending.length);

    for (let i = 0; i < pending.length; i++) {
      const track = pending[i];
      setTracks((prev) =>
        prev.map((t) => (t.id === track.id ? { ...t, status: 'uploading' } : t))
      );

      try {
        const trackId = generateTrackId();
        const mp3Blob = await writeMp3Metadata(track.file, {
          title: track.metadata.title,
          album: track.metadata.album,
          duration: track.metadata.duration,
          pictureBlob: track.metadata.pictureBlob,
          year: new Date().getFullYear(),
          genre: track.category,
          trackId,
        });
        const mp3File = new File([mp3Blob], track.file.name, {
          type: 'audio/mpeg',
        });

        const uploadYear = new Date().getFullYear();
        const storagePath = `music/${uploadYear}/${track.file.name}`;
        await uploadFile(storagePath, mp3File);

        const trackData: CreateMusicTrack = {
          title: track.metadata.title,
          artist: 'Dark Torus',
          album: track.metadata.album,
          albumArtist: track.metadata.albumArtist,
          category: track.category,
          storagePath,
          duration: track.metadata.duration,
          bitrate: track.metadata.bitrate,
          sampleRate: track.metadata.sampleRate,
          numberOfChannels: track.metadata.numberOfChannels,
          codec: track.metadata.codec,
          year: uploadYear,
          genre: track.category,
          comment: buildComment(trackId),
          bpm: track.metadata.bpm,
          trackNo: track.metadata.trackNo,
          diskNo: track.metadata.diskNo,
          composer: track.metadata.composer,
          lyricist: track.metadata.lyricist,
          isrc: track.metadata.isrc,
          originalFilename: track.file.name,
          fileSize: track.file.size,
        };

        await addMusicTrack(trackData, currentUser.uid, trackId);

        if (track.metadata.pictureBlob) {
          // Обрезаем изображение до квадрата по центру перед загрузкой
          const croppedBlob = await cropImageToSquare(track.metadata.pictureBlob, 'image/jpeg');
          const coverPath = `covers/${trackId}.jpg`;
          await uploadFile(coverPath, croppedBlob);
          await updateMusicTrack(trackId, { coverPath });
        }

        setTracks((prev) => {
          const item = prev.find((x) => x.id === track.id);
          if (item?.metadata.pictureUrl) {
            URL.revokeObjectURL(item.metadata.pictureUrl);
          }
          return prev.filter((t) => t.id !== track.id);
        });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setTracks((prev) =>
          prev.map((t) =>
            t.id === track.id ? { ...t, status: 'error', error: message } : t
          )
        );
      }

      setUploadingCount((c) => c - 1);
    }
  }, [tracks, currentUser]);

  const pendingCount = tracks.filter((t) => t.status === 'pending').length;

  return (
    <SidebarProvider
      style={
        {
          '--sidebar-width': '16rem',
          '--sidebar-width-icon': '3rem',
          '--header-height': 'calc(var(--spacing) * 12)',
        } as React.CSSProperties
      }
    >
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader title="Upload Tracks" />

        <div className="flex flex-1 flex-col p-4 lg:p-6">
          <div className="mb-4 flex flex-wrap items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/app/music')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-lg font-semibold">Upload Tracks</h2>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-muted-foreground">Жанр для всех:</span>
              <Select
                value={globalCategory}
                onValueChange={(v) => {
                  setGlobalCategory(v as MusicCategory);
                  setTracks((prev) =>
                    prev.map((t) =>
                      t.status === 'pending' ? { ...t, category: v as MusicCategory } : t
                    )
                  );
                }}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MUSIC_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={cn(
              'mb-6 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 transition-colors',
              isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
            )}
          >
            <input
              type="file"
              accept=".mp3,audio/mpeg"
              multiple
              onChange={handleFileInput}
              className="hidden"
              id="upload-files"
            />
            <Music className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground mb-2">
              Drag & drop MP3 files here, or
            </p>
            <Button asChild variant="outline">
              <label htmlFor="upload-files" className="cursor-pointer">
                Select files
              </label>
            </Button>
          </div>

          {tracks.length > 0 && (
            <>
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {tracks.length} track(s) • {pendingCount} pending
                </p>
                <Button
                  onClick={uploadAll}
                  disabled={pendingCount === 0 || uploadingCount > 0}
                >
                  {uploadingCount > 0 ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading... ({uploadingCount} left)
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload all ({pendingCount})
                    </>
                  )}
                </Button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {tracks.map((track) => (
                  <div
                    key={track.id}
                    className={cn(
                      'flex gap-4 rounded-lg border p-4',
                      track.status === 'error' && 'border-destructive'
                    )}
                  >
                    <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md bg-muted">
                      {track.metadata.pictureUrl ? (
                        <img
                          src={track.metadata.pictureUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <Music className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}
                      {track.status === 'uploading' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                          <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                      )}
                      {track.status === 'done' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-green-500/20">
                          <span className="text-lg">✓</span>
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{track.metadata.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {track.metadata.artist || 'Unknown'}
                        {track.metadata.album && ` • ${track.metadata.album}`}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDuration(track.metadata.duration)} • {track.category}
                      </p>

                      {track.status === 'error' && track.error && (
                        <p className="mt-1 text-xs text-destructive">{track.error}</p>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={() => removeTrack(track.id)}
                      disabled={track.status === 'uploading'}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
